import * as yup$1 from 'yup';
import { date as date$4, number, addMethod, string, bool, mixed, setLocale, reach } from 'yup';
import { parse, isValid, formatISO, isToday } from 'date-fns';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _inheritsLoose from '@babel/runtime/helpers/esm/inheritsLoose';

var float = function float(input) {
  if (typeof input === 'string' && /[^\-\d,.]/g.test(input)) {
    return null;
  } else {
    var safeInput = input === undefined || input === null ? '' : input;
    var parsed = parseFloat(safeInput.toString().replace(/,/g, ''));
    return isNaN(parsed) || ("" + safeInput).indexOf('.') !== ("" + safeInput).lastIndexOf('.') ? null : parsed;
  }
};
var int = function int(input) {
  if (input === void 0) {
    input = '';
  }

  var parsedAsFloat = float(input);
  return parsedAsFloat === null ? null : Math.round(parsedAsFloat);
}; // we only support MMDDYYYY and YYYYMMDD eight-digit formats in that order; if an eight-digit
// input does not match either of these formats, we throw it out as invalid

var SUPPORTED_EIGHT_DIGIT_FORMATS = ['MMddyyyy', 'yyyyMMdd'];

var parseEightDigitFormats = function parseEightDigitFormats(input) {
  return SUPPORTED_EIGHT_DIGIT_FORMATS.reduce(function (validDate, format) {
    if (validDate) {
      return validDate;
    }

    var parsed = parse(input, format, new Date());
    return isValid(parsed) ? parsed : null;
  }, null);
};

var date = function date(input) {
  // yup does not parse dates in MMDDYYYY format, so we use the date-fns parser to handle
  // this format and other eight-digit formats
  if (/^\d{8}$/.test(input)) {
    return parseEightDigitFormats(input);
  }

  try {
    return date$4().cast(input) || null;
  } catch (e) {
    return null;
  }
};

var parsers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    float: float,
    int: int,
    date: date
});

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq_1(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

var _assocIndexOf = assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return _assocIndexOf(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

var _listCacheSet = listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = _listCacheClear;
ListCache.prototype['delete'] = _listCacheDelete;
ListCache.prototype.get = _listCacheGet;
ListCache.prototype.has = _listCacheHas;
ListCache.prototype.set = _listCacheSet;

var _ListCache = ListCache;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new _ListCache;
  this.size = 0;
}

var _stackClear = stackClear;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas;

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/** Built-in value references. */
var Symbol = _root.Symbol;

var _Symbol = Symbol;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = _baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction;

/** Used to detect overreaching core-js shims. */
var coreJsData = _root['__core-js_shared__'];

var _coreJsData = coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(_coreJsData && _coreJsData.keys && _coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

var _isMasked = isMasked;

/** Used for built-in method references. */
var funcProto = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

var _toSource = toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$2 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject_1(value) || _isMasked(value)) {
    return false;
  }
  var pattern = isFunction_1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(_toSource(value));
}

var _baseIsNative = baseIsNative;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = _getValue(object, key);
  return _baseIsNative(value) ? value : undefined;
}

var _getNative = getNative;

/* Built-in method references that are verified to be native. */
var Map = _getNative(_root, 'Map');

var _Map = Map;

/* Built-in method references that are verified to be native. */
var nativeCreate = _getNative(Object, 'create');

var _nativeCreate = nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = _nativeCreate ? _nativeCreate(null) : {};
  this.size = 0;
}

var _hashClear = hashClear;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (_nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$2.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet;

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return _nativeCreate ? (data[key] !== undefined) : hasOwnProperty$3.call(data, key);
}

var _hashHas = hashHas;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (_nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = _hashClear;
Hash.prototype['delete'] = _hashDelete;
Hash.prototype.get = _hashGet;
Hash.prototype.has = _hashHas;
Hash.prototype.set = _hashSet;

var _Hash = Hash;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new _Hash,
    'map': new (_Map || _ListCache),
    'string': new _Hash
  };
}

var _mapCacheClear = mapCacheClear;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

var _isKeyable = isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return _isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

var _getMapData = getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = _getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return _getMapData(this, key).get(key);
}

var _mapCacheGet = mapCacheGet;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return _getMapData(this, key).has(key);
}

var _mapCacheHas = mapCacheHas;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = _getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = _mapCacheClear;
MapCache.prototype['delete'] = _mapCacheDelete;
MapCache.prototype.get = _mapCacheGet;
MapCache.prototype.has = _mapCacheHas;
MapCache.prototype.set = _mapCacheSet;

var _MapCache = MapCache;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof _ListCache) {
    var pairs = data.__data__;
    if (!_Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new _MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new _ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = _stackClear;
Stack.prototype['delete'] = _stackDelete;
Stack.prototype.get = _stackGet;
Stack.prototype.has = _stackHas;
Stack.prototype.set = _stackSet;

var _Stack = Stack;

/**
 * A specialized version of `_.forEach` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns `array`.
 */
function arrayEach(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }
  return array;
}

var _arrayEach = arrayEach;

var defineProperty = (function() {
  try {
    var func = _getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var _defineProperty = defineProperty;

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && _defineProperty) {
    _defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$4.call(object, key) && eq_1(objValue, value)) ||
      (value === undefined && !(key in object))) {
    _baseAssignValue(object, key, value);
  }
}

var _assignValue = assignValue;

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      _baseAssignValue(object, key, newValue);
    } else {
      _assignValue(object, key, newValue);
    }
  }
  return object;
}

var _copyObject = copyObject;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

var _baseTimes = baseTimes;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == argsTag;
}

var _baseIsArguments = baseIsArguments;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$6.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$6.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = _baseIsArguments(function() { return arguments; }()) ? _baseIsArguments : function(value) {
  return isObjectLike_1(value) && hasOwnProperty$5.call(value, 'callee') &&
    !propertyIsEnumerable.call(value, 'callee');
};

var isArguments_1 = isArguments;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

var isArray_1 = isArray;

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

var isBuffer_1 = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse_1;

module.exports = isBuffer;
});

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

var isLength_1 = isLength;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    objectTag = '[object Object]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] =
typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
typedArrayTags[errorTag] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag] = typedArrayTags[numberTag] =
typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
typedArrayTags[setTag] = typedArrayTags[stringTag] =
typedArrayTags[weakMapTag] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike_1(value) &&
    isLength_1(value.length) && !!typedArrayTags[_baseGetTag(value)];
}

var _baseIsTypedArray = baseIsTypedArray;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

var _baseUnary = baseUnary;

var _nodeUtil = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports && _freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

module.exports = nodeUtil;
});

/* Node.js helper references. */
var nodeIsTypedArray = _nodeUtil && _nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? _baseUnary(nodeIsTypedArray) : _baseIsTypedArray;

var isTypedArray_1 = isTypedArray;

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray_1(value),
      isArg = !isArr && isArguments_1(value),
      isBuff = !isArr && !isArg && isBuffer_1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray_1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? _baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$6.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           _isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

var _arrayLikeKeys = arrayLikeKeys;

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$8;

  return value === proto;
}

var _isPrototype = isPrototype;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = _overArg(Object.keys, Object);

var _nativeKeys = nativeKeys;

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$9.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!_isPrototype(object)) {
    return _nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$7.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

var _baseKeys = baseKeys;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}

var isArrayLike_1 = isArrayLike;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object) : _baseKeys(object);
}

var keys_1 = keys;

/**
 * The base implementation of `_.assign` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssign(object, source) {
  return object && _copyObject(source, keys_1(source), object);
}

var _baseAssign = baseAssign;

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function nativeKeysIn(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

var _nativeKeysIn = nativeKeysIn;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$a.hasOwnProperty;

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn(object) {
  if (!isObject_1(object)) {
    return _nativeKeysIn(object);
  }
  var isProto = _isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$8.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

var _baseKeysIn = baseKeysIn;

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object, true) : _baseKeysIn(object);
}

var keysIn_1 = keysIn;

/**
 * The base implementation of `_.assignIn` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssignIn(object, source) {
  return object && _copyObject(source, keysIn_1(source), object);
}

var _baseAssignIn = baseAssignIn;

var _cloneBuffer = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined,
    allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined;

/**
 * Creates a clone of  `buffer`.
 *
 * @private
 * @param {Buffer} buffer The buffer to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Buffer} Returns the cloned buffer.
 */
function cloneBuffer(buffer, isDeep) {
  if (isDeep) {
    return buffer.slice();
  }
  var length = buffer.length,
      result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

  buffer.copy(result);
  return result;
}

module.exports = cloneBuffer;
});

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */
function copyArray(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

var _copyArray = copyArray;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var _arrayFilter = arrayFilter;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

var stubArray_1 = stubArray;

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$b.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray_1 : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return _arrayFilter(nativeGetSymbols(object), function(symbol) {
    return propertyIsEnumerable$1.call(object, symbol);
  });
};

var _getSymbols = getSymbols;

/**
 * Copies own symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbols(source, object) {
  return _copyObject(source, _getSymbols(source), object);
}

var _copySymbols = copySymbols;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

var _arrayPush = arrayPush;

/** Built-in value references. */
var getPrototype = _overArg(Object.getPrototypeOf, Object);

var _getPrototype = getPrototype;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols$1 = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own and inherited enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbolsIn = !nativeGetSymbols$1 ? stubArray_1 : function(object) {
  var result = [];
  while (object) {
    _arrayPush(result, _getSymbols(object));
    object = _getPrototype(object);
  }
  return result;
};

var _getSymbolsIn = getSymbolsIn;

/**
 * Copies own and inherited symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbolsIn(source, object) {
  return _copyObject(source, _getSymbolsIn(source), object);
}

var _copySymbolsIn = copySymbolsIn;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray_1(object) ? result : _arrayPush(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return _baseGetAllKeys(object, keys_1, _getSymbols);
}

var _getAllKeys = getAllKeys;

/**
 * Creates an array of own and inherited enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeysIn(object) {
  return _baseGetAllKeys(object, keysIn_1, _getSymbolsIn);
}

var _getAllKeysIn = getAllKeysIn;

/* Built-in method references that are verified to be native. */
var DataView = _getNative(_root, 'DataView');

var _DataView = DataView;

/* Built-in method references that are verified to be native. */
var Promise = _getNative(_root, 'Promise');

var _Promise = Promise;

/* Built-in method references that are verified to be native. */
var Set = _getNative(_root, 'Set');

var _Set = Set;

/* Built-in method references that are verified to be native. */
var WeakMap = _getNative(_root, 'WeakMap');

var _WeakMap = WeakMap;

/** `Object#toString` result references. */
var mapTag$1 = '[object Map]',
    objectTag$1 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$1 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';

var dataViewTag$1 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = _toSource(_DataView),
    mapCtorString = _toSource(_Map),
    promiseCtorString = _toSource(_Promise),
    setCtorString = _toSource(_Set),
    weakMapCtorString = _toSource(_WeakMap);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag = _baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((_DataView && getTag(new _DataView(new ArrayBuffer(1))) != dataViewTag$1) ||
    (_Map && getTag(new _Map) != mapTag$1) ||
    (_Promise && getTag(_Promise.resolve()) != promiseTag) ||
    (_Set && getTag(new _Set) != setTag$1) ||
    (_WeakMap && getTag(new _WeakMap) != weakMapTag$1)) {
  getTag = function(value) {
    var result = _baseGetTag(value),
        Ctor = result == objectTag$1 ? value.constructor : undefined,
        ctorString = Ctor ? _toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$1;
        case mapCtorString: return mapTag$1;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$1;
        case weakMapCtorString: return weakMapTag$1;
      }
    }
    return result;
  };
}

var _getTag = getTag;

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$c.hasOwnProperty;

/**
 * Initializes an array clone.
 *
 * @private
 * @param {Array} array The array to clone.
 * @returns {Array} Returns the initialized clone.
 */
function initCloneArray(array) {
  var length = array.length,
      result = new array.constructor(length);

  // Add properties assigned by `RegExp#exec`.
  if (length && typeof array[0] == 'string' && hasOwnProperty$9.call(array, 'index')) {
    result.index = array.index;
    result.input = array.input;
  }
  return result;
}

var _initCloneArray = initCloneArray;

/** Built-in value references. */
var Uint8Array = _root.Uint8Array;

var _Uint8Array = Uint8Array;

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new _Uint8Array(result).set(new _Uint8Array(arrayBuffer));
  return result;
}

var _cloneArrayBuffer = cloneArrayBuffer;

/**
 * Creates a clone of `dataView`.
 *
 * @private
 * @param {Object} dataView The data view to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned data view.
 */
function cloneDataView(dataView, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(dataView.buffer) : dataView.buffer;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}

var _cloneDataView = cloneDataView;

/** Used to match `RegExp` flags from their coerced string values. */
var reFlags = /\w*$/;

/**
 * Creates a clone of `regexp`.
 *
 * @private
 * @param {Object} regexp The regexp to clone.
 * @returns {Object} Returns the cloned regexp.
 */
function cloneRegExp(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}

var _cloneRegExp = cloneRegExp;

/** Used to convert symbols to primitives and strings. */
var symbolProto = _Symbol ? _Symbol.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

/**
 * Creates a clone of the `symbol` object.
 *
 * @private
 * @param {Object} symbol The symbol object to clone.
 * @returns {Object} Returns the cloned symbol object.
 */
function cloneSymbol(symbol) {
  return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
}

var _cloneSymbol = cloneSymbol;

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray(typedArray, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

var _cloneTypedArray = cloneTypedArray;

/** `Object#toString` result references. */
var boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    mapTag$2 = '[object Map]',
    numberTag$1 = '[object Number]',
    regexpTag$1 = '[object RegExp]',
    setTag$2 = '[object Set]',
    stringTag$1 = '[object String]',
    symbolTag = '[object Symbol]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$2 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';

/**
 * Initializes an object clone based on its `toStringTag`.
 *
 * **Note:** This function only supports cloning values with tags of
 * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
 *
 * @private
 * @param {Object} object The object to clone.
 * @param {string} tag The `toStringTag` of the object to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneByTag(object, tag, isDeep) {
  var Ctor = object.constructor;
  switch (tag) {
    case arrayBufferTag$1:
      return _cloneArrayBuffer(object);

    case boolTag$1:
    case dateTag$1:
      return new Ctor(+object);

    case dataViewTag$2:
      return _cloneDataView(object, isDeep);

    case float32Tag$1: case float64Tag$1:
    case int8Tag$1: case int16Tag$1: case int32Tag$1:
    case uint8Tag$1: case uint8ClampedTag$1: case uint16Tag$1: case uint32Tag$1:
      return _cloneTypedArray(object, isDeep);

    case mapTag$2:
      return new Ctor;

    case numberTag$1:
    case stringTag$1:
      return new Ctor(object);

    case regexpTag$1:
      return _cloneRegExp(object);

    case setTag$2:
      return new Ctor;

    case symbolTag:
      return _cloneSymbol(object);
  }
}

var _initCloneByTag = initCloneByTag;

/** Built-in value references. */
var objectCreate = Object.create;

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate = (function() {
  function object() {}
  return function(proto) {
    if (!isObject_1(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

var _baseCreate = baseCreate;

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject(object) {
  return (typeof object.constructor == 'function' && !_isPrototype(object))
    ? _baseCreate(_getPrototype(object))
    : {};
}

var _initCloneObject = initCloneObject;

/** `Object#toString` result references. */
var mapTag$3 = '[object Map]';

/**
 * The base implementation of `_.isMap` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 */
function baseIsMap(value) {
  return isObjectLike_1(value) && _getTag(value) == mapTag$3;
}

var _baseIsMap = baseIsMap;

/* Node.js helper references. */
var nodeIsMap = _nodeUtil && _nodeUtil.isMap;

/**
 * Checks if `value` is classified as a `Map` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 * @example
 *
 * _.isMap(new Map);
 * // => true
 *
 * _.isMap(new WeakMap);
 * // => false
 */
var isMap = nodeIsMap ? _baseUnary(nodeIsMap) : _baseIsMap;

var isMap_1 = isMap;

/** `Object#toString` result references. */
var setTag$3 = '[object Set]';

/**
 * The base implementation of `_.isSet` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 */
function baseIsSet(value) {
  return isObjectLike_1(value) && _getTag(value) == setTag$3;
}

var _baseIsSet = baseIsSet;

/* Node.js helper references. */
var nodeIsSet = _nodeUtil && _nodeUtil.isSet;

/**
 * Checks if `value` is classified as a `Set` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 * @example
 *
 * _.isSet(new Set);
 * // => true
 *
 * _.isSet(new WeakSet);
 * // => false
 */
var isSet = nodeIsSet ? _baseUnary(nodeIsSet) : _baseIsSet;

var isSet_1 = isSet;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG = 1,
    CLONE_FLAT_FLAG = 2,
    CLONE_SYMBOLS_FLAG = 4;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag$2 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    mapTag$4 = '[object Map]',
    numberTag$2 = '[object Number]',
    objectTag$2 = '[object Object]',
    regexpTag$2 = '[object RegExp]',
    setTag$4 = '[object Set]',
    stringTag$2 = '[object String]',
    symbolTag$1 = '[object Symbol]',
    weakMapTag$2 = '[object WeakMap]';

var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$3 = '[object DataView]',
    float32Tag$2 = '[object Float32Array]',
    float64Tag$2 = '[object Float64Array]',
    int8Tag$2 = '[object Int8Array]',
    int16Tag$2 = '[object Int16Array]',
    int32Tag$2 = '[object Int32Array]',
    uint8Tag$2 = '[object Uint8Array]',
    uint8ClampedTag$2 = '[object Uint8ClampedArray]',
    uint16Tag$2 = '[object Uint16Array]',
    uint32Tag$2 = '[object Uint32Array]';

/** Used to identify `toStringTag` values supported by `_.clone`. */
var cloneableTags = {};
cloneableTags[argsTag$2] = cloneableTags[arrayTag$1] =
cloneableTags[arrayBufferTag$2] = cloneableTags[dataViewTag$3] =
cloneableTags[boolTag$2] = cloneableTags[dateTag$2] =
cloneableTags[float32Tag$2] = cloneableTags[float64Tag$2] =
cloneableTags[int8Tag$2] = cloneableTags[int16Tag$2] =
cloneableTags[int32Tag$2] = cloneableTags[mapTag$4] =
cloneableTags[numberTag$2] = cloneableTags[objectTag$2] =
cloneableTags[regexpTag$2] = cloneableTags[setTag$4] =
cloneableTags[stringTag$2] = cloneableTags[symbolTag$1] =
cloneableTags[uint8Tag$2] = cloneableTags[uint8ClampedTag$2] =
cloneableTags[uint16Tag$2] = cloneableTags[uint32Tag$2] = true;
cloneableTags[errorTag$1] = cloneableTags[funcTag$2] =
cloneableTags[weakMapTag$2] = false;

/**
 * The base implementation of `_.clone` and `_.cloneDeep` which tracks
 * traversed objects.
 *
 * @private
 * @param {*} value The value to clone.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Deep clone
 *  2 - Flatten inherited properties
 *  4 - Clone symbols
 * @param {Function} [customizer] The function to customize cloning.
 * @param {string} [key] The key of `value`.
 * @param {Object} [object] The parent object of `value`.
 * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
 * @returns {*} Returns the cloned value.
 */
function baseClone(value, bitmask, customizer, key, object, stack) {
  var result,
      isDeep = bitmask & CLONE_DEEP_FLAG,
      isFlat = bitmask & CLONE_FLAT_FLAG,
      isFull = bitmask & CLONE_SYMBOLS_FLAG;

  if (customizer) {
    result = object ? customizer(value, key, object, stack) : customizer(value);
  }
  if (result !== undefined) {
    return result;
  }
  if (!isObject_1(value)) {
    return value;
  }
  var isArr = isArray_1(value);
  if (isArr) {
    result = _initCloneArray(value);
    if (!isDeep) {
      return _copyArray(value, result);
    }
  } else {
    var tag = _getTag(value),
        isFunc = tag == funcTag$2 || tag == genTag$1;

    if (isBuffer_1(value)) {
      return _cloneBuffer(value, isDeep);
    }
    if (tag == objectTag$2 || tag == argsTag$2 || (isFunc && !object)) {
      result = (isFlat || isFunc) ? {} : _initCloneObject(value);
      if (!isDeep) {
        return isFlat
          ? _copySymbolsIn(value, _baseAssignIn(result, value))
          : _copySymbols(value, _baseAssign(result, value));
      }
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }
      result = _initCloneByTag(value, tag, isDeep);
    }
  }
  // Check for circular references and return its corresponding clone.
  stack || (stack = new _Stack);
  var stacked = stack.get(value);
  if (stacked) {
    return stacked;
  }
  stack.set(value, result);

  if (isSet_1(value)) {
    value.forEach(function(subValue) {
      result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap_1(value)) {
    value.forEach(function(subValue, key) {
      result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
    });
  }

  var keysFunc = isFull
    ? (isFlat ? _getAllKeysIn : _getAllKeys)
    : (isFlat ? keysIn_1 : keys_1);

  var props = isArr ? undefined : keysFunc(value);
  _arrayEach(props || value, function(subValue, key) {
    if (props) {
      key = subValue;
      subValue = value[key];
    }
    // Recursively populate clone (susceptible to call stack limits).
    _assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
  });
  return result;
}

var _baseClone = baseClone;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG$1 = 1,
    CLONE_SYMBOLS_FLAG$1 = 4;

/**
 * This method is like `_.clone` except that it recursively clones `value`.
 *
 * @static
 * @memberOf _
 * @since 1.0.0
 * @category Lang
 * @param {*} value The value to recursively clone.
 * @returns {*} Returns the deep cloned value.
 * @see _.clone
 * @example
 *
 * var objects = [{ 'a': 1 }, { 'b': 2 }];
 *
 * var deep = _.cloneDeep(objects);
 * console.log(deep[0] === objects[0]);
 * // => false
 */
function cloneDeep(value) {
  return _baseClone(value, CLONE_DEEP_FLAG$1 | CLONE_SYMBOLS_FLAG$1);
}

var cloneDeep_1 = cloneDeep;

/** `Object#toString` result references. */
var symbolTag$2 = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike_1(value) && _baseGetTag(value) == symbolTag$2);
}

var isSymbol_1 = isSymbol;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray_1(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol_1(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

var _isKey = isKey;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || _MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = _MapCache;

var memoize_1 = memoize;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize_1(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

var _memoizeCapped = memoizeCapped;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = _memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

var _stringToPath = stringToPath;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

var _arrayMap = arrayMap;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = _Symbol ? _Symbol.prototype : undefined,
    symbolToString = symbolProto$1 ? symbolProto$1.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray_1(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return _arrayMap(value, baseToString) + '';
  }
  if (isSymbol_1(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

var _baseToString = baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : _baseToString(value);
}

var toString_1 = toString;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray_1(value)) {
    return value;
  }
  return _isKey(value, object) ? [value] : _stringToPath(toString_1(value));
}

var _castPath = castPath;

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol_1(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

var _toKey = toKey;

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = _castPath(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[_toKey(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

var _baseGet = baseGet;

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : _baseGet(object, path);
  return result === undefined ? defaultValue : result;
}

var get_1 = get;

/**
 * The base implementation of `_.set`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @param {Function} [customizer] The function to customize path creation.
 * @returns {Object} Returns `object`.
 */
function baseSet(object, path, value, customizer) {
  if (!isObject_1(object)) {
    return object;
  }
  path = _castPath(path, object);

  var index = -1,
      length = path.length,
      lastIndex = length - 1,
      nested = object;

  while (nested != null && ++index < length) {
    var key = _toKey(path[index]),
        newValue = value;

    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      return object;
    }

    if (index != lastIndex) {
      var objValue = nested[key];
      newValue = customizer ? customizer(objValue, key, nested) : undefined;
      if (newValue === undefined) {
        newValue = isObject_1(objValue)
          ? objValue
          : (_isIndex(path[index + 1]) ? [] : {});
      }
    }
    _assignValue(nested, key, newValue);
    nested = nested[key];
  }
  return object;
}

var _baseSet = baseSet;

/**
 * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
 * it's created. Arrays are created for missing index properties while objects
 * are created for all other missing properties. Use `_.setWith` to customize
 * `path` creation.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.set(object, 'a[0].b.c', 4);
 * console.log(object.a[0].b.c);
 * // => 4
 *
 * _.set(object, ['x', '0', 'y', 'z'], 5);
 * console.log(object.x[0].y.z);
 * // => 5
 */
function set(object, path, value) {
  return object == null ? object : _baseSet(object, path, value);
}

var set_1 = set;

/** `Object#toString` result references. */
var objectTag$3 = '[object Object]';

/** Used for built-in method references. */
var funcProto$2 = Function.prototype,
    objectProto$d = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$2 = funcProto$2.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$a = objectProto$d.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString$2.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject(value) {
  if (!isObjectLike_1(value) || _baseGetTag(value) != objectTag$3) {
    return false;
  }
  var proto = _getPrototype(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty$a.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString$2.call(Ctor) == objectCtorString;
}

var isPlainObject_1 = isPlainObject;

var formatSingleField = function formatSingleField(resource, key, formatMapping) {
  var value = _typeof(resource) === 'object' && !Array.isArray(resource) ? get_1(resource, key) : resource;
  var formatter = get_1(formatMapping, key);

  if (formatter && typeof formatter === 'function') {
    return formatter(value);
  }

  return value;
};
/**
 * @param resource {Object} - Resource to be deeply formatted
 * @param formatMapping {Object} - Map of key/values, where the key is a dot notated key for a value in the resource, and the value is the formatting function
 * @param skipUndefinedOrNull {Bool} [true] - Whether or not to run null or undefined properties through the formatter, if the formatter is defined, it will be
 * executed and the result set in the object. This is useful for transforming nulls/undefined into a useful value (i.e. null residences -> [])
 */

var deeplyFormat = function deeplyFormat(resource, formatMapping, skipUndefinedOrNull) {
  if (resource === void 0) {
    resource = {};
  }

  if (skipUndefinedOrNull === void 0) {
    skipUndefinedOrNull = true;
  }

  var clonedResource = cloneDeep_1(resource);
  Object.keys(formatMapping).forEach(function (key) {
    var value = get_1(clonedResource, key);

    if (!(skipUndefinedOrNull && (value === null || value === undefined))) {
      if (isPlainObject_1(value) && typeof get_1(formatMapping, key) !== 'function') {
        set_1(clonedResource, key, deeplyFormat(value, get_1(formatMapping, key), skipUndefinedOrNull));
      } else {
        set_1(clonedResource, key, formatSingleField(clonedResource, key, formatMapping));
      }
    }
  });
  return clonedResource;
};

var stripZeroes = function stripZeroes(input) {
  var trailingZeroes = input.match(/\.0+$/);
  return trailingZeroes ? input.substr(0, trailingZeroes.index) : input;
}; // https://stackoverflow.com/q/41045270/112765


var clampFractionDigits = function clampFractionDigits(digits) {
  return Math.max(0, Math.min(20, digits));
};

var float$1 = function float$1(input, decimalPlaces, dropTrailingZeroes) {
  if (dropTrailingZeroes === void 0) {
    dropTrailingZeroes = false;
  }

  // We use our own parser since parseFloat does not handle `1,000` for instance
  var safeInput = typeof input === 'string' ? float(input) : input;

  if (safeInput === undefined || safeInput === null || isNaN(safeInput) || ("" + input).indexOf('.') !== input.length - 1 && ("" + input).substr(-1) === '.') {
    return null;
  }

  if (("" + input).substr(-1) === '.') {
    return input;
  }

  var decimalCount = decimalPlaces;
  var parsedDecimalPlaces = parseInt(decimalPlaces);

  if (isNaN(parsedDecimalPlaces)) {
    var inputDecimals = input.toString().split('.')[1];
    decimalCount = inputDecimals ? inputDecimals.length : 0;
  }

  var clampedFractionDigits = clampFractionDigits(decimalCount);
  var toFormat = safeInput.toLocaleString('en-US', {
    minimumFractionDigits: clampedFractionDigits,
    maximumFractionDigits: clampedFractionDigits
  });
  return dropTrailingZeroes ? stripZeroes(toFormat) : toFormat;
};
var int$1 = function int(input) {
  return float$1(input, 0);
};
var dollar = function dollar(input, decimals) {
  if (decimals === void 0) {
    decimals = 0;
  }

  if (float$1(input) === null) {
    return null;
  }

  var clampedFractionDigits = clampFractionDigits(decimals);
  return float(input).toLocaleString('en-US', {
    currency: 'USD',
    style: 'currency',
    minimumFractionDigits: clampedFractionDigits,
    maximumFractionDigits: clampedFractionDigits
  });
};
var percentage = function percentage(value, decimalPlaces) {
  if (decimalPlaces === void 0) {
    decimalPlaces = 0;
  }

  var floatResult = value || value === 0 ? float$1(value * 100, decimalPlaces) : null;
  return floatResult ? floatResult + "%" : null;
};
var date$1 = function date$1(input) {
  var safeDate = date(input);
  return safeDate ? safeDate.getMonth() + 1 + "/" + safeDate.getDate() + "/" + safeDate.getFullYear() : null;
};
var dateRange = function dateRange(inputStartDate, inputEndDate) {
  var startDate = date$1(inputStartDate);
  var endDate = date$1(inputEndDate);

  if (!startDate && !endDate) {
    return 'Unknown';
  }

  if (startDate && endDate) {
    return startDate + " - " + endDate;
  }

  if (startDate) {
    return startDate + " - Present";
  }

  return "Through " + endDate;
};
var datetime = function datetime(input) {
  var safeDate = date(input);
  return safeDate ? safeDate.toLocaleString('en-us') : null;
};
var isoDate = function isoDate(input) {
  return input ? formatISO(input, {
    representation: 'date'
  }) : null;
};
var phoneNumber = function phoneNumber(input) {
  if (typeof input !== 'string') {
    return null;
  } // ensure we are only working with digits/+


  var digits = input.replace(/([^\d+])/g, '');
  var matches = digits.match(/^(\+?1)?([2-9]\d{0,2})(\d{1,3})?(\d*)?$/);

  if (!matches) {
    return null;
  }

  var _matches = _slicedToArray(matches, 5),
      countryCode = _matches[1],
      areaCode = _matches[2],
      firstThree = _matches[3],
      rest = _matches[4];

  return [countryCode ? countryCode + " " : '', areaCode ? "(" + areaCode : '', firstThree ? ") " + firstThree : '', rest ? "-" + rest : ''].join('');
};
var socialSecurityNumber = function socialSecurityNumber(input) {
  if (typeof input !== 'string') {
    return null;
  }

  var digits = input.replace(/(^\+1)|(\D)/g, '');
  var matches = digits.match(/^(\d{1,3})-?(\d{1,2})?-?(\d*)?$/);

  if (!matches) {
    return null;
  }

  return matches[1] + (!matches[2] ? '' : matches[3] ? "-" + matches[2] + "-" + matches[3] : "-" + matches[2]);
};
var twoDecimal = function twoDecimal(input) {
  return float$1(input, 2, true);
};
var accountingNotationNumber = function accountingNotationNumber(input, decimalPlaces, dropTrailingZeroes) {
  if (decimalPlaces === void 0) {
    decimalPlaces = 2;
  }

  if (dropTrailingZeroes === void 0) {
    dropTrailingZeroes = true;
  }

  var safeInput = typeof input === 'number' ? input : float(input);

  if (typeof safeInput !== 'number') {
    return input;
  }

  return safeInput < 0 ? "(" + float$1(Math.abs(safeInput), decimalPlaces, dropTrailingZeroes) + ")" : float$1(safeInput, decimalPlaces, dropTrailingZeroes);
};

var standardFormatters = /*#__PURE__*/Object.freeze({
    __proto__: null,
    formatSingleField: formatSingleField,
    deeplyFormat: deeplyFormat,
    float: float$1,
    int: int$1,
    dollar: dollar,
    percentage: percentage,
    date: date$1,
    dateRange: dateRange,
    datetime: datetime,
    isoDate: isoDate,
    phoneNumber: phoneNumber,
    socialSecurityNumber: socialSecurityNumber,
    twoDecimal: twoDecimal,
    accountingNotationNumber: accountingNotationNumber
});

var standardFormatters$1 = [date$1, isoDate, socialSecurityNumber, phoneNumber, twoDecimal, int$1, float$1]; // Standard formatters return null when formatting fails. In contrast, input formatters return the original
// input when formatting fails. This way, users can see the original input that resulted in an error.

var getInputFormatter = function getInputFormatter(formatter) {
  return function (input) {
    var _formatter;

    for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      rest[_key - 1] = arguments[_key];
    }

    return (_formatter = formatter.apply(void 0, [input].concat(rest))) !== null && _formatter !== void 0 ? _formatter : input;
  };
};

var _standardFormatters$m = standardFormatters$1.map(getInputFormatter),
    _standardFormatters$m2 = _slicedToArray(_standardFormatters$m, 7),
    date$2 = _standardFormatters$m2[0],
    isoDate$1 = _standardFormatters$m2[1],
    socialSecurityNumber$1 = _standardFormatters$m2[2],
    phoneNumber$1 = _standardFormatters$m2[3],
    twoDecimal$1 = _standardFormatters$m2[4],
    int$2 = _standardFormatters$m2[5],
    float$2 = _standardFormatters$m2[6];

var inputFormatters = /*#__PURE__*/Object.freeze({
    __proto__: null,
    date: date$2,
    isoDate: isoDate$1,
    socialSecurityNumber: socialSecurityNumber$1,
    phoneNumber: phoneNumber$1,
    twoDecimal: twoDecimal$1,
    int: int$2,
    float: float$2
});

// sort object properties so they can be used to populate select inputs in the correct order
var sortObjectPropertiesByValues = (function (objectToSort) {
  return Object.entries(objectToSort).sort(function (_ref, _ref2) {
    var _ref3 = _slicedToArray(_ref, 2),
        firstValue = _ref3[1];

    var _ref4 = _slicedToArray(_ref2, 2),
        secondValue = _ref4[1];

    return firstValue.localeCompare(secondValue);
  }).reduce(function (acc, _ref5) {
    var _Object$assign;

    var _ref6 = _slicedToArray(_ref5, 2),
        key = _ref6[0],
        value = _ref6[1];

    return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[key] = value, _Object$assign));
  }, {});
});

var _sortObjectProperties, _sortObjectProperties2;
var DEFAULT_ERROR_MESSAGES = {
  DATE: 'Invalid date',
  EMAIL: 'Must be a valid email address',
  PHONE: 'Must be a valid phone number',
  SSN: 'Must be a valid Social Security number',
  VALUE: 'Invalid value'
};
var EMPTY_SELECT_VALUE = 'empty';
var STRING_TRUE = 'true';
var STRING_FALSE = 'false';
var BOOLEAN_STRINGS = [STRING_TRUE, STRING_FALSE];
var NULLABLE_BOOLEAN_STRINGS = [].concat(BOOLEAN_STRINGS, [null]);
var COUNTRIES_LOOKUP = {
  US: 'US'
};
var COUNTRIES = sortObjectPropertiesByValues((_sortObjectProperties = {}, _sortObjectProperties[COUNTRIES_LOOKUP.US] = 'United States', _sortObjectProperties.AD = 'Andorra', _sortObjectProperties.AE = 'United Arab Emirates', _sortObjectProperties.AF = 'Afghanistan', _sortObjectProperties.AG = 'Antigua and Barbuda', _sortObjectProperties.AI = 'Anguilla', _sortObjectProperties.AL = 'Albania', _sortObjectProperties.AM = 'Armenia', _sortObjectProperties.AO = 'Angola', _sortObjectProperties.AR = 'Argentina', _sortObjectProperties.AS = 'American Samoa', _sortObjectProperties.AT = 'Austria', _sortObjectProperties.AU = 'Australia', _sortObjectProperties.AW = 'Aruba', _sortObjectProperties.AX = 'Åland Islands', _sortObjectProperties.AZ = 'Azerbaijan', _sortObjectProperties.BA = 'Bosnia and Herzegovina', _sortObjectProperties.BB = 'Barbados', _sortObjectProperties.BD = 'Bangladesh', _sortObjectProperties.BE = 'Belgium', _sortObjectProperties.BF = 'Burkina Faso', _sortObjectProperties.BG = 'Bulgaria', _sortObjectProperties.BH = 'Bahrain', _sortObjectProperties.BI = 'Burundi', _sortObjectProperties.BJ = 'Benin', _sortObjectProperties.BL = 'Saint Barthélemy', _sortObjectProperties.BM = 'Bermuda', _sortObjectProperties.BN = 'Brunei Darussalam', _sortObjectProperties.BO = 'Bolivia', _sortObjectProperties.BQ = 'Bonaire, Sint Eustatius, and Saba', _sortObjectProperties.BR = 'Brazil', _sortObjectProperties.BS = 'Bahamas', _sortObjectProperties.BT = 'Bhutan', _sortObjectProperties.BV = 'Bouvet Island', _sortObjectProperties.BW = 'Botswana', _sortObjectProperties.BY = 'Belarus', _sortObjectProperties.BZ = 'Belize', _sortObjectProperties.CA = 'Canada', _sortObjectProperties.CC = 'Cocos (Keeling) Islands', _sortObjectProperties.CD = 'Democratic Republic of the Congo', _sortObjectProperties.CF = 'Central African Republic', _sortObjectProperties.CG = 'Congo', _sortObjectProperties.CH = 'Switzerland', _sortObjectProperties.CI = "Côte d'Ivoire", _sortObjectProperties.CK = 'Cook Islands', _sortObjectProperties.CL = 'Chile', _sortObjectProperties.CM = 'Cameroon', _sortObjectProperties.CN = 'China', _sortObjectProperties.CO = 'Colombia', _sortObjectProperties.CR = 'Costa Rica', _sortObjectProperties.CU = 'Cuba', _sortObjectProperties.CV = 'Cabo Verde', _sortObjectProperties.CW = 'Curaçao', _sortObjectProperties.CX = 'Christmas Island', _sortObjectProperties.CY = 'Cyprus', _sortObjectProperties.CZ = 'Czechia', _sortObjectProperties.DE = 'Germany', _sortObjectProperties.DJ = 'Djibouti', _sortObjectProperties.DK = 'Denmark', _sortObjectProperties.DM = 'Dominica', _sortObjectProperties.DO = 'Dominican Republic', _sortObjectProperties.DZ = 'Algeria', _sortObjectProperties.EC = 'Ecuador', _sortObjectProperties.EE = 'Estonia', _sortObjectProperties.EG = 'Egypt', _sortObjectProperties.EH = 'Western Sahara', _sortObjectProperties.ER = 'Eritrea', _sortObjectProperties.ES = 'Spain', _sortObjectProperties.ET = 'Ethiopia', _sortObjectProperties.FI = 'Finland', _sortObjectProperties.FJ = 'Fiji', _sortObjectProperties.FK = 'Falkland Islands', _sortObjectProperties.FM = 'Federated States of Micronesia', _sortObjectProperties.FO = 'Faroe Islands', _sortObjectProperties.FR = 'France', _sortObjectProperties.GA = 'Gabon', _sortObjectProperties.GB = 'United Kingdom', _sortObjectProperties.GD = 'Grenada', _sortObjectProperties.GE = 'Georgia', _sortObjectProperties.GF = 'French Guiana', _sortObjectProperties.GG = 'Guernsey', _sortObjectProperties.GH = 'Ghana', _sortObjectProperties.GI = 'Gibraltar', _sortObjectProperties.GL = 'Greenland', _sortObjectProperties.GM = 'Gambia', _sortObjectProperties.GN = 'Guinea', _sortObjectProperties.GP = 'Guadeloupe', _sortObjectProperties.GQ = 'Equatorial Guinea', _sortObjectProperties.GR = 'Greece', _sortObjectProperties.GS = 'South Georgia and the South Sandwich Islands', _sortObjectProperties.GT = 'Guatemala', _sortObjectProperties.GU = 'Guam', _sortObjectProperties.GW = 'Guinea-Bissau', _sortObjectProperties.GY = 'Guyana', _sortObjectProperties.HK = 'Hong Kong', _sortObjectProperties.HM = 'Heard Island and McDonald Islands', _sortObjectProperties.HN = 'Honduras', _sortObjectProperties.HR = 'Croatia', _sortObjectProperties.HT = 'Haiti', _sortObjectProperties.HU = 'Hungary', _sortObjectProperties.ID = 'Indonesia', _sortObjectProperties.IE = 'Ireland', _sortObjectProperties.IL = 'Israel', _sortObjectProperties.IM = 'Isle of Man', _sortObjectProperties.IN = 'India', _sortObjectProperties.IQ = 'Iraq', _sortObjectProperties.IR = 'Iran', _sortObjectProperties.IS = 'Iceland', _sortObjectProperties.IT = 'Italy', _sortObjectProperties.JE = 'Jersey', _sortObjectProperties.JM = 'Jamaica', _sortObjectProperties.JO = 'Jordan', _sortObjectProperties.JP = 'Japan', _sortObjectProperties.KE = 'Kenya', _sortObjectProperties.KG = 'Kyrgyzstan', _sortObjectProperties.KH = 'Cambodia', _sortObjectProperties.KI = 'Kiribati', _sortObjectProperties.KM = 'Comoros', _sortObjectProperties.KN = 'Saint Kitts and Nevis', _sortObjectProperties.KP = 'North Korea', _sortObjectProperties.KR = 'South Korea', _sortObjectProperties.KW = 'Kuwait', _sortObjectProperties.KY = 'Cayman Islands', _sortObjectProperties.KZ = 'Kazakhstan', _sortObjectProperties.LA = 'Laos', _sortObjectProperties.LB = 'Lebanon', _sortObjectProperties.LC = 'Saint Lucia', _sortObjectProperties.LI = 'Liechtenstein', _sortObjectProperties.LK = 'Sri Lanka', _sortObjectProperties.LR = 'Liberia', _sortObjectProperties.LS = 'Lesotho', _sortObjectProperties.LT = 'Lithuania', _sortObjectProperties.LU = 'Luxembourg', _sortObjectProperties.LV = 'Latvia', _sortObjectProperties.LY = 'Libya', _sortObjectProperties.MA = 'Morocco', _sortObjectProperties.MC = 'Monaco', _sortObjectProperties.MD = 'Moldova', _sortObjectProperties.ME = 'Montenegro', _sortObjectProperties.MF = 'Saint Martin', _sortObjectProperties.MG = 'Madagascar', _sortObjectProperties.MH = 'Marshall Islands', _sortObjectProperties.MK = 'North Macedonia', _sortObjectProperties.ML = 'Mali', _sortObjectProperties.MM = 'Myanmar', _sortObjectProperties.MN = 'Mongolia', _sortObjectProperties.MO = 'Macao', _sortObjectProperties.MP = 'Northern Mariana Islands', _sortObjectProperties.MQ = 'Martinique', _sortObjectProperties.MR = 'Mauritania', _sortObjectProperties.MS = 'Montserrat', _sortObjectProperties.MT = 'Malta', _sortObjectProperties.MU = 'Mauritius', _sortObjectProperties.MV = 'Maldives', _sortObjectProperties.MW = 'Malawi', _sortObjectProperties.MX = 'Mexico', _sortObjectProperties.MY = 'Malaysia', _sortObjectProperties.MZ = 'Mozambique', _sortObjectProperties.NA = 'Namibia', _sortObjectProperties.NC = 'New Caledonia', _sortObjectProperties.NE = 'Niger', _sortObjectProperties.NF = 'Norfolk Island', _sortObjectProperties.NG = 'Nigeria', _sortObjectProperties.NI = 'Nicaragua', _sortObjectProperties.NL = 'Netherlands', _sortObjectProperties.NO = 'Norway', _sortObjectProperties.NP = 'Nepal', _sortObjectProperties.NR = 'Nauru', _sortObjectProperties.NU = 'Niue', _sortObjectProperties.NZ = 'New Zealand', _sortObjectProperties.OM = 'Oman', _sortObjectProperties.PA = 'Panama', _sortObjectProperties.PE = 'Peru', _sortObjectProperties.PF = 'French Polynesia', _sortObjectProperties.PG = 'Papua New Guinea', _sortObjectProperties.PH = 'Philippines', _sortObjectProperties.PK = 'Pakistan', _sortObjectProperties.PL = 'Poland', _sortObjectProperties.PM = 'Saint Pierre and Miquelon', _sortObjectProperties.PN = 'Pitcairn', _sortObjectProperties.PR = 'Puerto Rico', _sortObjectProperties.PS = 'Palestine', _sortObjectProperties.PT = 'Portugal', _sortObjectProperties.PW = 'Palau', _sortObjectProperties.PY = 'Paraguay', _sortObjectProperties.QA = 'Qatar', _sortObjectProperties.RE = 'Réunion', _sortObjectProperties.RO = 'Romania', _sortObjectProperties.RS = 'Serbia', _sortObjectProperties.RU = 'Russia', _sortObjectProperties.RW = 'Rwanda', _sortObjectProperties.SA = 'Saudi Arabia', _sortObjectProperties.SB = 'Solomon Islands', _sortObjectProperties.SC = 'Seychelles', _sortObjectProperties.SD = 'Sudan', _sortObjectProperties.SE = 'Sweden', _sortObjectProperties.SG = 'Singapore', _sortObjectProperties.SH = 'Saint Helena, Ascension, and Tristan da Cunha', _sortObjectProperties.SI = 'Slovenia', _sortObjectProperties.SJ = 'Svalbard and Jan Mayen', _sortObjectProperties.SK = 'Slovakia', _sortObjectProperties.SL = 'Sierra Leone', _sortObjectProperties.SM = 'San Marino', _sortObjectProperties.SN = 'Senegal', _sortObjectProperties.SO = 'Somalia', _sortObjectProperties.SR = 'Suriname', _sortObjectProperties.SS = 'South Sudan', _sortObjectProperties.ST = 'Sao Tome and Principe', _sortObjectProperties.SV = 'El Salvador', _sortObjectProperties.SX = 'Sint Maarten', _sortObjectProperties.SY = 'Syria', _sortObjectProperties.SZ = 'Eswatini', _sortObjectProperties.TC = 'Turks and Caicos', _sortObjectProperties.TD = 'Chad', _sortObjectProperties.TF = 'French Southern Territories', _sortObjectProperties.TG = 'Togo', _sortObjectProperties.TH = 'Thailand', _sortObjectProperties.TJ = 'Tajikistan', _sortObjectProperties.TK = 'Tokelau', _sortObjectProperties.TL = 'Timor-Leste', _sortObjectProperties.TM = 'Turkmenistan', _sortObjectProperties.TN = 'Tunisia', _sortObjectProperties.TO = 'Tonga', _sortObjectProperties.TR = 'Turkey', _sortObjectProperties.TT = 'Trinidad and Tobago', _sortObjectProperties.TV = 'Tuvalu', _sortObjectProperties.TW = 'Taiwan', _sortObjectProperties.TZ = 'Tanzania', _sortObjectProperties.UA = 'Ukraine', _sortObjectProperties.UG = 'Uganda', _sortObjectProperties.UM = 'U.S. Minor Outlying Islands', _sortObjectProperties.UY = 'Uruguay', _sortObjectProperties.UZ = 'Uzbekistan', _sortObjectProperties.VA = 'Holy See', _sortObjectProperties.VC = 'Saint Vincent and the Grenadines', _sortObjectProperties.VE = 'Venezuela', _sortObjectProperties.VG = 'British Virgin Islands', _sortObjectProperties.VI = 'U.S. Virgin Islands', _sortObjectProperties.VN = 'Viet Nam', _sortObjectProperties.VU = 'Vanuatu', _sortObjectProperties.WF = 'Wallis and Futuna', _sortObjectProperties.WS = 'Samoa', _sortObjectProperties.YE = 'Yemen', _sortObjectProperties.YT = 'Mayotte', _sortObjectProperties.ZA = 'South Africa', _sortObjectProperties.ZM = 'Zambia', _sortObjectProperties.ZW = 'Zimbabwe', _sortObjectProperties));
var STATES_LOOKUP = {
  AK: 'AK',
  AL: 'AL',
  AR: 'AR',
  AZ: 'AZ',
  CA: 'CA',
  CO: 'CO',
  CT: 'CT',
  DC: 'DC',
  DE: 'DE',
  FL: 'FL',
  GA: 'GA',
  HI: 'HI',
  IA: 'IA',
  ID: 'ID',
  IL: 'IL',
  IN: 'IN',
  KS: 'KS',
  KY: 'KY',
  LA: 'LA',
  MA: 'MA',
  MD: 'MD',
  ME: 'ME',
  MI: 'MI',
  MN: 'MN',
  MO: 'MO',
  MS: 'MS',
  MT: 'MT',
  NC: 'NC',
  ND: 'ND',
  NE: 'NE',
  NH: 'NH',
  NJ: 'NJ',
  NM: 'NM',
  NV: 'NV',
  NY: 'NY',
  OH: 'OH',
  OK: 'OK',
  OR: 'OR',
  PA: 'PA',
  RI: 'RI',
  SC: 'SC',
  SD: 'SD',
  TN: 'TN',
  TX: 'TX',
  UT: 'UT',
  VA: 'VA',
  VT: 'VT',
  WA: 'WA',
  WI: 'WI',
  WV: 'WV',
  WY: 'WY'
};
var STATES = sortObjectPropertiesByValues((_sortObjectProperties2 = {}, _sortObjectProperties2[STATES_LOOKUP.AK] = 'Alaska', _sortObjectProperties2[STATES_LOOKUP.AL] = 'Alabama', _sortObjectProperties2[STATES_LOOKUP.AR] = 'Arkansas', _sortObjectProperties2[STATES_LOOKUP.AZ] = 'Arizona', _sortObjectProperties2[STATES_LOOKUP.CA] = 'California', _sortObjectProperties2[STATES_LOOKUP.CO] = 'Colorado', _sortObjectProperties2[STATES_LOOKUP.CT] = 'Connecticut', _sortObjectProperties2[STATES_LOOKUP.DC] = 'District of Columbia', _sortObjectProperties2[STATES_LOOKUP.DE] = 'Delaware', _sortObjectProperties2[STATES_LOOKUP.FL] = 'Florida', _sortObjectProperties2[STATES_LOOKUP.GA] = 'Georgia', _sortObjectProperties2[STATES_LOOKUP.HI] = 'Hawaii', _sortObjectProperties2[STATES_LOOKUP.IA] = 'Iowa', _sortObjectProperties2[STATES_LOOKUP.ID] = 'Idaho', _sortObjectProperties2[STATES_LOOKUP.IL] = 'Illinois', _sortObjectProperties2[STATES_LOOKUP.IN] = 'Indiana', _sortObjectProperties2[STATES_LOOKUP.KS] = 'Kansas', _sortObjectProperties2[STATES_LOOKUP.KY] = 'Kentucky', _sortObjectProperties2[STATES_LOOKUP.LA] = 'Louisiana', _sortObjectProperties2[STATES_LOOKUP.MA] = 'Massachusetts', _sortObjectProperties2[STATES_LOOKUP.MD] = 'Maryland', _sortObjectProperties2[STATES_LOOKUP.ME] = 'Maine', _sortObjectProperties2[STATES_LOOKUP.MI] = 'Michigan', _sortObjectProperties2[STATES_LOOKUP.MN] = 'Minnesota', _sortObjectProperties2[STATES_LOOKUP.MO] = 'Missouri', _sortObjectProperties2[STATES_LOOKUP.MS] = 'Mississippi', _sortObjectProperties2[STATES_LOOKUP.MT] = 'Montana', _sortObjectProperties2[STATES_LOOKUP.NC] = 'North Carolina', _sortObjectProperties2[STATES_LOOKUP.ND] = 'North Dakota', _sortObjectProperties2[STATES_LOOKUP.NE] = 'Nebraska', _sortObjectProperties2[STATES_LOOKUP.NH] = 'New Hampshire', _sortObjectProperties2[STATES_LOOKUP.NJ] = 'New Jersey', _sortObjectProperties2[STATES_LOOKUP.NM] = 'New Mexico', _sortObjectProperties2[STATES_LOOKUP.NV] = 'Nevada', _sortObjectProperties2[STATES_LOOKUP.NY] = 'New York', _sortObjectProperties2[STATES_LOOKUP.OH] = 'Ohio', _sortObjectProperties2[STATES_LOOKUP.OK] = 'Oklahoma', _sortObjectProperties2[STATES_LOOKUP.OR] = 'Oregon', _sortObjectProperties2[STATES_LOOKUP.PA] = 'Pennsylvania', _sortObjectProperties2[STATES_LOOKUP.RI] = 'Rhode Island', _sortObjectProperties2[STATES_LOOKUP.SC] = 'South Carolina', _sortObjectProperties2[STATES_LOOKUP.SD] = 'South Dakota', _sortObjectProperties2[STATES_LOOKUP.TN] = 'Tennessee', _sortObjectProperties2[STATES_LOOKUP.TX] = 'Texas', _sortObjectProperties2[STATES_LOOKUP.UT] = 'Utah', _sortObjectProperties2[STATES_LOOKUP.VA] = 'Virginia', _sortObjectProperties2[STATES_LOOKUP.VT] = 'Vermont', _sortObjectProperties2[STATES_LOOKUP.WA] = 'Washington', _sortObjectProperties2[STATES_LOOKUP.WI] = 'Wisconsin', _sortObjectProperties2[STATES_LOOKUP.WV] = 'West Virginia', _sortObjectProperties2[STATES_LOOKUP.WY] = 'Wyoming', _sortObjectProperties2));
var STATE_CODES = sortObjectPropertiesByValues(STATES_LOOKUP);

var constants = /*#__PURE__*/Object.freeze({
    __proto__: null,
    DEFAULT_ERROR_MESSAGES: DEFAULT_ERROR_MESSAGES,
    EMPTY_SELECT_VALUE: EMPTY_SELECT_VALUE,
    STRING_TRUE: STRING_TRUE,
    STRING_FALSE: STRING_FALSE,
    BOOLEAN_STRINGS: BOOLEAN_STRINGS,
    NULLABLE_BOOLEAN_STRINGS: NULLABLE_BOOLEAN_STRINGS,
    COUNTRIES_LOOKUP: COUNTRIES_LOOKUP,
    COUNTRIES: COUNTRIES,
    STATES_LOOKUP: STATES_LOOKUP,
    STATES: STATES,
    STATE_CODES: STATE_CODES
});

var _SCHEMA_TYPES, _SCHEMA_SUB_TYPES;

var SCHEMA_TYPES_LOOKUP = {
  DATE: 'DATE',
  NUMBER: 'NUMBER',
  BOOLEAN: 'BOOLEAN',
  STRING: 'STRING',
  MIXED: 'MIXED'
};
var SCHEMA_TYPES = (_SCHEMA_TYPES = {}, _SCHEMA_TYPES[SCHEMA_TYPES_LOOKUP.DATE] = 'date', _SCHEMA_TYPES[SCHEMA_TYPES_LOOKUP.NUMBER] = 'number', _SCHEMA_TYPES[SCHEMA_TYPES_LOOKUP.BOOLEAN] = 'boolean', _SCHEMA_TYPES[SCHEMA_TYPES_LOOKUP.STRING] = 'string', _SCHEMA_TYPES[SCHEMA_TYPES_LOOKUP.MIXED] = 'mixed', _SCHEMA_TYPES);
var SCHEMA_SUB_TYPES = (_SCHEMA_SUB_TYPES = {}, _SCHEMA_SUB_TYPES[SCHEMA_TYPES_LOOKUP.STRING] = {
  SOCIAL_SECURITY_NUMBER: 'SOCIAL_SECURITY_NUMBER',
  PHONE_NUMBER: 'PHONE_NUMBER',
  EMAIL: 'EMAIL',
  NAME_FIRST: 'NAME_FIRST',
  NAME_LAST: 'NAME_LAST',
  ADDRESS_STREET: 'ADDRESS_STREET',
  ADDRESS_UNIT: 'ADDRESS_UNIT',
  ADDRESS_CITY: 'ADDRESS_CITY',
  ADDRESS_SUBDIVISION: 'ADDRESS_SUBDIVISION',
  ADDRESS_CODE: 'ADDRESS_CODE',
  ADDRESS_COUNTRY: 'ADDRESS_COUNTRY'
}, _SCHEMA_SUB_TYPES[SCHEMA_TYPES_LOOKUP.NUMBER] = {
  DOLLAR: 'DOLLAR'
}, _SCHEMA_SUB_TYPES);

var types = /*#__PURE__*/Object.freeze({
    __proto__: null,
    SCHEMA_TYPES: SCHEMA_TYPES,
    SCHEMA_SUB_TYPES: SCHEMA_SUB_TYPES
});

var float$3 = /*#__PURE__*/function (_yup$number) {
  _inheritsLoose(float$1, _yup$number);

  function float$1() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _yup$number.call.apply(_yup$number, [this].concat(args)) || this;

    _this.withMutation(function () {
      _this.transforms = [];

      _this.transform(function (value) {
        var parsed = float(value);

        if (parsed === null) {
          return value;
        }

        return _this.isType(value) ? value : parsed;
      });
    });

    return _this;
  }

  return float$1;
}(number);

var date$3 = /*#__PURE__*/function (_yup$date) {
  _inheritsLoose(date$1, _yup$date);

  function date$1() {
    var _this2;

    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    _this2 = _yup$date.call.apply(_yup$date, [this].concat(args)) || this;

    _this2.withMutation(function () {
      _this2.transforms = [];

      _this2.transform(function (current, original) {
        var _dateParser;

        if (_this2.isType(original)) {
          return current;
        }

        return (_dateParser = date(original)) !== null && _dateParser !== void 0 ? _dateParser : new Date('');
      });
    });

    return _this2;
  }

  return date$1;
}(date$4);

var NULLABLE_DATE_VALUES = ['', null];

var nullResolvingDate = /*#__PURE__*/function (_date) {
  _inheritsLoose(nullResolvingDate, _date);

  function nullResolvingDate() {
    var _this3;

    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }

    _this3 = _date.call.apply(_date, [this].concat(args)) || this;

    _this3.withMutation(function () {
      _this3.transforms = [];

      _this3.transform(function (current, original) {
        var _dateParser2;

        if (_this3.isType(original)) {
          return current;
        } else if (NULLABLE_DATE_VALUES.some(function (value) {
          return value === current;
        })) {
          return null;
        }

        return (_dateParser2 = date(original)) !== null && _dateParser2 !== void 0 ? _dateParser2 : new Date('');
      });
    });

    return _this3;
  }

  return nullResolvingDate;
}(date$3);

addMethod(number, 'convertEmptyToZero', function () {
  return this.transform(function (value) {
    return value === '' ? 0 : value;
  });
});
addMethod(number, 'convertEmptyToNull', function () {
  return this.transform(function (value) {
    return value === '' ? null : value;
  });
});
addMethod(number, 'dollar', function () {
  return this.meta({
    subType: SCHEMA_SUB_TYPES.NUMBER.DOLLAR
  });
});
addMethod(date$4, 'convertEmptyAndNullToUndefined', function () {
  return this.transform(function (current, original) {
    return original === '' || original === null ? undefined : current;
  });
});
addMethod(string, 'stripNonNumeric', function () {
  return this.transform(function (current, original) {
    return original === null ? '' : current.replace(/\D/g, '');
  });
});
addMethod(string, 'code', function (message) {
  return string().matches(/^\d{5}(-\d{4})?$/, {
    message: message,
    excludeEmptyString: true
  });
});
addMethod(string, 'phone', function (message) {
  if (message === void 0) {
    message = DEFAULT_ERROR_MESSAGES.PHONE;
  }

  return string().stripNonNumeric().matches(/^\+?1?[\s-]?(\(?[2-9][0-8]\d{1}\)?)[\s-]?([2-9]\d{2})([\s-]?\d{4})$/, {
    message: message,
    excludeEmptyString: true
  }).meta({
    subType: SCHEMA_SUB_TYPES.STRING.PHONE_NUMBER
  });
});
addMethod(string, 'socialSecurityNumber', function (message) {
  if (message === void 0) {
    message = DEFAULT_ERROR_MESSAGES.SSN;
  }

  return string().stripNonNumeric().matches(/^[0-9]{9}$/, {
    message: message,
    excludeEmptyString: true
  }).meta({
    subType: SCHEMA_SUB_TYPES.STRING.SOCIAL_SECURITY_NUMBER
  });
});
addMethod(string, 'nullableOneOf', function (enumList) {
  return string().transform(function (current, original) {
    return original === EMPTY_SELECT_VALUE || original === '' ? null : current;
  }).oneOf([].concat(_toConsumableArray(enumList), [null])).nullable();
});
addMethod(bool, 'nullableSelect', function () {
  return bool().transform(function (current, original) {
    return original === EMPTY_SELECT_VALUE ? null : current;
  }).nullable();
});
addMethod(mixed, 'onlyWhen', function (fields, whenCallback) {
  var _this4 = this;

  return this.when(fields, function () {
    for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
      args[_key4] = arguments[_key4];
    }

    var values = args.slice(0, typeof fields === 'string' ? 1 : fields.length);
    var whenFulfilled = whenCallback ? whenCallback.apply(void 0, _toConsumableArray(values)) : values.some(Boolean);
    return whenFulfilled ? _this4 : _this4.strip();
  });
});

var defaultKeyTransform = function defaultKeyTransform(key) {
  return key;
};

addMethod(mixed, 'enumeration', function (values, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      _options$includeEmpty = _options.includeEmptyOption,
      includeEmptyOption = _options$includeEmpty === void 0 ? false : _options$includeEmpty,
      _options$transformKey = _options.transformKey,
      transformKey = _options$transformKey === void 0 ? defaultKeyTransform : _options$transformKey;
  var requirednessMethod = includeEmptyOption ? 'nullable' : 'required';
  var schema = mixed().transform(function (current, original) {
    return original === EMPTY_SELECT_VALUE || original === '' ? null : transformKey(current);
  }).oneOf(Object.keys(values).map(transformKey).concat(includeEmptyOption ? null : [])).meta({
    enumeration: {
      values: values,
      includeEmptyOption: includeEmptyOption
    }
  });
  return schema[requirednessMethod]();
});

var camelCaseToSentenceCaseLastProperty = function camelCaseToSentenceCaseLastProperty(path) {
  var lastProperty = path.split('.').slice(-1)[0];
  var lowerCaseFirstSentenceCase = lastProperty.replace(/([A-Z])/g, ' $1').toLowerCase();
  return lowerCaseFirstSentenceCase.charAt(0).toUpperCase() + lowerCaseFirstSentenceCase.slice(1);
};

setLocale({
  mixed: {
    notType: function notType(_ref) {
      var type = _ref.type,
          label = _ref.label,
          originalValue = _ref.originalValue;

      if (originalValue === undefined || originalValue === '') {
        return DEFAULT_ERROR_MESSAGES.VALUE;
      }

      switch (type) {
        case 'number':
          return label ? label + " is not a valid number" : "'" + originalValue + "' is not a valid number";

        case 'date':
          return DEFAULT_ERROR_MESSAGES.DATE;

        default:
          return DEFAULT_ERROR_MESSAGES.VALUE;
      }
    },
    oneOf: function oneOf(_ref2) {
      var label = _ref2.label,
          path = _ref2.path;
      return (label || camelCaseToSentenceCaseLastProperty(path)) + " must be selected";
    },
    required: function required(_ref3) {
      var label = _ref3.label,
          path = _ref3.path;
      return (label || camelCaseToSentenceCaseLastProperty(path)) + " is required";
    }
  },
  string: {
    matches: function matches(_ref4) {
      var label = _ref4.label,
          path = _ref4.path;
      return "Invalid value for " + (label || camelCaseToSentenceCaseLastProperty(path));
    },
    email: DEFAULT_ERROR_MESSAGES.EMAIL
  },
  number: {
    min: function min(_ref5) {
      var _min = _ref5.min,
          label = _ref5.label,
          path = _ref5.path;
      return (label || camelCaseToSentenceCaseLastProperty(path)) + " must be greater than or equal to " + float$1(_min, 2, true);
    },
    lessThan: function lessThan(_ref6) {
      var less = _ref6.less,
          label = _ref6.label,
          path = _ref6.path;
      return (label || camelCaseToSentenceCaseLastProperty(path)) + " must be less than " + float$1(less, 2, true);
    },
    max: function max(_ref7) {
      var _max = _ref7.max,
          label = _ref7.label,
          path = _ref7.path;
      return (label || camelCaseToSentenceCaseLastProperty(path)) + " must be less than or equal to " + float$1(_max, 2, true);
    },
    positive: function positive(_ref8) {
      var label = _ref8.label,
          path = _ref8.path;
      return (label || camelCaseToSentenceCaseLastProperty(path)) + " must be a positive number";
    }
  },
  date: {
    max: function max(_ref9) {
      var _max2 = _ref9.max,
          label = _ref9.label,
          path = _ref9.path;
      return (label || camelCaseToSentenceCaseLastProperty(path)) + " must not be " + (isToday(_max2) ? 'in the future' : "after " + date$1(_max2));
    },
    min: function min(_ref10) {
      var _min2 = _ref10.min,
          label = _ref10.label,
          path = _ref10.path;
      return (label || camelCaseToSentenceCaseLastProperty(path)) + " must not be " + (isToday(_min2) ? 'in the past' : "before " + date$1(_min2));
    }
  }
});
var yup = Object.assign({}, yup$1, {
  float: float$3,
  date: date$3,
  nullResolvingDate: nullResolvingDate
});

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

var identity_1 = identity;

var _FORMATTER_FOR_SCHEMA, _FORMATTER_FOR_SCHEMA2;
var REQUIRED_TEST_NAME = 'required';

var introspectResolvedSchema = function introspectResolvedSchema(schema, name) {
  if (schema.spec.strip) {
    return null;
  }

  var _schema$describe = schema.describe(),
      type = _schema$describe.type,
      label = _schema$describe.label,
      tests = _schema$describe.tests,
      _schema$describe$meta = _schema$describe.meta;

  _schema$describe$meta = _schema$describe$meta === void 0 ? {} : _schema$describe$meta;
  var enumeration = _schema$describe$meta.enumeration,
      subType = _schema$describe$meta.subType;
  var required = tests.some(function (_ref) {
    var testName = _ref.name;
    return testName === REQUIRED_TEST_NAME;
  });
  return {
    name: name,
    schemaType: type,
    schemaSubType: subType,
    label: label,
    required: required,
    enumeration: enumeration
  };
};

var introspectSchema = function introspectSchema(schema, values, name) {
  var resolvedSchema = schema === null || schema === void 0 ? void 0 : schema.resolve({
    parent: values
  });

  if (resolvedSchema !== null && resolvedSchema !== void 0 && resolvedSchema.fields) {
    return Object.keys(resolvedSchema.fields).reduce(function (acc, key) {
      var _Object$assign;

      return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[key] = introspectSchema(reach(resolvedSchema, key), name ? get_1(values, name) : values, name ? name + "." + key : key), _Object$assign));
    }, {});
  }

  return resolvedSchema ? introspectResolvedSchema(resolvedSchema, name) : null;
};
var FORMATTER_FOR_SCHEMA_TYPE = (_FORMATTER_FOR_SCHEMA = {}, _FORMATTER_FOR_SCHEMA[SCHEMA_TYPES.DATE] = date$2, _FORMATTER_FOR_SCHEMA[SCHEMA_TYPES.NUMBER] = float$2, _FORMATTER_FOR_SCHEMA);
var FORMATTER_FOR_SCHEMA_SUB_TYPE = (_FORMATTER_FOR_SCHEMA2 = {}, _FORMATTER_FOR_SCHEMA2[SCHEMA_SUB_TYPES.STRING.SOCIAL_SECURITY_NUMBER] = socialSecurityNumber$1, _FORMATTER_FOR_SCHEMA2[SCHEMA_SUB_TYPES.STRING.PHONE_NUMBER] = phoneNumber$1, _FORMATTER_FOR_SCHEMA2[SCHEMA_SUB_TYPES.NUMBER.DOLLAR] = twoDecimal$1, _FORMATTER_FOR_SCHEMA2);
var getFieldFormattersFromSchema = function getFieldFormattersFromSchema(schema) {
  var _ref4, _FORMATTER_FOR_SCHEMA3;

  if (schema !== null && schema !== void 0 && schema.fields) {
    return Object.entries(schema.fields).reduce(function (acc, _ref2) {
      var _Object$assign2;

      var _ref3 = _slicedToArray(_ref2, 2),
          key = _ref3[0],
          value = _ref3[1];

      return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[key] = getFieldFormattersFromSchema(value), _Object$assign2));
    }, {});
  }

  var _schema$describe2 = schema.describe(),
      type = _schema$describe2.type,
      _schema$describe2$met = _schema$describe2.meta;

  _schema$describe2$met = _schema$describe2$met === void 0 ? {} : _schema$describe2$met;
  var subType = _schema$describe2$met.subType;
  return (_ref4 = (_FORMATTER_FOR_SCHEMA3 = FORMATTER_FOR_SCHEMA_SUB_TYPE[subType]) !== null && _FORMATTER_FOR_SCHEMA3 !== void 0 ? _FORMATTER_FOR_SCHEMA3 : FORMATTER_FOR_SCHEMA_TYPE[type]) !== null && _ref4 !== void 0 ? _ref4 : identity_1;
};

var introspect = /*#__PURE__*/Object.freeze({
    __proto__: null,
    introspectSchema: introspectSchema,
    getFieldFormattersFromSchema: getFieldFormattersFromSchema
});

var formatters = Object.assign({}, standardFormatters, {
  inputFormatters: inputFormatters
});

export default yup;
export { constants, formatters, introspect, parsers, types };
